import Image from 'next/image';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

interface ProcedureGlanceData {
  heading?: {
    text?: string;
    title?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          // site_content
          title: string;
          text: string;
          
          // images
          alt: string;
          image_file: {
            image: {
              id: string;
              filename_download: string;
            };
          };
        };
      }[];
    };
  }[];
}

interface ProcedureGlanceProps {
  data?: ProcedureGlanceData;
}

export const fields = [
  // title
  "title",
  
  // headings
  "heading.text",
  "heading.title",
  
  // content collection for glance items
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  
  "content_collection.site_content_collections_id.collection.item:images.alt",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.id",
  "content_collection.site_content_collections_id.collection.item:images.image_file.image.filename_download",
] as const;

export default function ProcedureGlance({ data }: ProcedureGlanceProps) {
  if (!data) return null;
  
  // Extract title or use a default
  const title = data.heading?.text || data.heading?.title || "";
  
  // Extract glance items from content collection
  const glanceItems = data.content_collection || [];
  
  // Default fallback icons based on position
  const fallbackIcons = [
    "/images/anesthesia.svg",
    "/images/length-of-surgery.svg",
    "/images/face-icon.svg",
    "/images/recovery.svg",
    "/images/out-patient-inpatient.svg"
  ];
  
  return (
    <div className="w-full bg-white pb-8 lg:pb-16">
      {/* At a Glance Section */}
      <div className="max-w-screen-2xl mx-auto px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-7">
            
            {/* Title */}
            <div className="rounded-[30px] p-5 flex flex-col items-center justify-center">
                <div className="text-2xl md:text-4xl text-primary font-medium font-greycliff">
                    {title}
                </div>
            </div>

            {/* Glance Items */}
            {glanceItems.map((collection, index) => {
              const imageItem = collection?.site_content_collections_id?.collection.find(item => item.collection === "images")?.item;
              const titleItem = collection?.site_content_collections_id?.collection.find(item => item.collection === "site_content" && item.item.title)?.item;
              const textItem = collection?.site_content_collections_id?.collection.find(item => item.collection === "site_content" && item.item.text)?.item;
              
              const imageId = imageItem?.image_file?.image?.id || "";
              const imageFilename = imageItem?.image_file?.image?.filename_download || "";
              const imageAlt = imageItem?.alt || "";
              const title = titleItem?.title || "";
              const text = textItem?.text || "";
              
              // Use appropriate fallback icon based on index
              const iconUrl = imageId 
                ? getReadableImageUrl(imageId, imageFilename)
                : fallbackIcons[index % fallbackIcons.length];

              return (
                <div key={index} className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src={iconUrl}
                      alt={imageAlt || title || ""}
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-greycliff lg:text-[1rem] md:text-xl mb-2 text-center">{title.toUpperCase()}</div>
                  <p className="text-primary font-greycliff text-[1rem] font-normal leading-normal md:text-base text-center [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-base" dangerouslySetInnerHTML={sanitizeHTML(text)} />
                </div>
              );
            })}
            
            {/* Fallback content if no items are provided */}
            {(!glanceItems || glanceItems.length === 0) && (
              <>
                {/* Anesthesia */}
                <div className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src="/images/anesthesia.svg"
                      alt="Anesthesia"
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-semibold text-base md:text-xl mb-2 text-center">Anesthesia:</div>
                  <p className="text-primary text-sm md:text-base text-center">General</p>
                </div>

                {/* Length of Surgery */}
                <div className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src="/images/length-of-surgery.svg"
                      alt="Surgery Length"
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-semibold text-base md:text-xl mb-2 text-center">Length of Surgery:</div>
                  <p className="text-primary text-sm md:text-base text-center">3-5 hours depending on the patient's needs</p>
                </div>

                {/* Outpatient/Inpatient */}
                <div className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src="/images/face-icon.svg"
                      alt="Patient Type"
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-semibold text-base md:text-xl mb-2 text-center">Outpatient / Inpatient:</div>
                  <p className="text-primary text-sm md:text-base text-center">Outpatient</p>
                </div>

                {/* Recovery */}
                <div className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src="/images/recovery.svg"
                      alt="Recovery"
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-semibold text-base md:text-xl mb-2 text-center">Recovery:</div>
                  <p className="text-primary text-sm md:text-base text-center">Initial recovery will take 1-2 weeks but full recovery takes up to 12 months</p>
                </div>

                {/* Combined Procedures */}
                <div className="rounded-[30px] border border-primary p-4 flex flex-col items-center hover:bg-gray-200">
                  <div className="w-16 h-16 mb-6">
                    <Image
                      src="/images/often-combined-with.svg"
                      alt="Combined Procedures"
                      width={64}
                      height={64}
                      quality={90}
                    />
                  </div>
                  <div className="text-primary font-semibold text-base md:text-xl mb-2 text-center">Often Combined With:</div>
                  <p className="text-primary text-sm md:text-base text-center">Septoplasty, Chin Augmentation</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 
